import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useAsync } from 'react-use';
import { useParams } from 'react-router';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Chip,
  IconButton,
  Switch,
  FormControlLabel,
  Box,
  Typography,
  Collapse,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  InputAdornment,
  CircularProgress,
  Paper,
  Backdrop,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import SearchIcon from '@material-ui/icons/Search';
import AddIcon from '@material-ui/icons/Add';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import { discoveryApiRef, fetchApiRef, microsoftAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { CATALOG_FILTER_EXISTS, catalogApiRef } from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import { useNotification } from '../../hooks/useNotification';
import { NotificationSnackbar } from '../../components/notification-snackbar/notification-snackbar';
import { SimpleConfirmDialog } from '../../components/dialog/confirm';
import { WorkspacesService } from '../../services/WorkspacesService';
import { WorkspaceSystemAdd } from './WorkspaceSystemAdd';
import { Addon, AddonComponent, CatalogData, ComponentType, Environment, ReprocessAddon, RetrySystemProvisionCommand, WorkspaceSystemItem } from './types';
import { GCPResourceAnnotations } from '@internal/backstage-plugin-gcp-common';
import { openControlReprocessPermission, openControlWritePermission } from '@internal/backstage-plugin-supply-features-common';

const useStyles = makeStyles(theme => ({
  filterContainer: {
    marginBottom: theme.spacing(2),
  },
  filterRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      '& > *': {
        width: '100%',
      },
    },
  },
  formControl: {
    minWidth: 200,
  },
  searchField: {
    minWidth: 300,
  },
  addButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    marginTop: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      width: 'auto',
      marginTop: 0,
      marginLeft: 'auto',
    },
  },
  addButton: {
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  table: {
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
  },
  expandCell: {
    width: 50,
  },
  statusActive: {
    backgroundColor: '#62AE6C',
    color: '#fff',
    fontWeight: 'bold',
  },
  statusInactive: {
    backgroundColor: '#BA564D',
    color: '#fff',
    fontWeight: 'bold',
  },
  addonRow: {
    backgroundColor: '#fafafa',
  },
  addonActions: {
    display: 'grid',
    gridTemplateColumns: 'auto 40px',
    columnGap: theme.spacing(2),
    alignItems: 'center',
  },
  addonReprocessColumn: {
    width: 40,
    display: 'flex',
    justifyContent: 'center',
  },
  addonContainer: {
    padding: theme.spacing(2),
  },
  tableContainer: {
    overflowX: 'auto',
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

type SystemInfo = {
  title: string;
  name: string;
  namespace: string;
  addons: string[];
  insightsFolder?: string;
  insightsResourceInfo: { name: string; resourceRef: string; type: ComponentType[] }[];
};

const isInsightsAddon = (addonId: string): boolean => addonId.toLowerCase() === 'add001';

export const WorkspaceSystem = ({ isWorkspaceActive }: { isWorkspaceActive?: boolean }) => {
  const classes = useStyles();
  const { environment, workspaceId } = useParams<{ environment: Environment; workspaceId: string }>();

  const catalogApi = useApi(catalogApiRef);
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const microsoftAuthApi = useApi(microsoftAuthApiRef);
  const { notification, showSuccess, showError, hideNotification } = useNotification();
  const { allowed: canCreate } = usePermission({ permission: openControlWritePermission });
  const { allowed: canReprocessTables } = usePermission({ permission: openControlReprocessPermission });

  const [expandedSystems, setExpandedSystems] = useState<Set<string>>(new Set());
  const [addonStatesOverride, setAddonStatesOverride] = useState<Record<string, string[]>>({});
  const [isTogglingAddon, setIsTogglingAddon] = useState(false);
  const [isRemovingSystem, setIsRemovingSystem] = useState(false);
  const [isReprocessingInsights, setIsReprocessingInsights] = useState(false);
  const [dialogData, setDialogData] = useState<{
    title: string;
    text: string;
    onConfirm: () => void;
  } | undefined>();
  const [statusFilter, setStatusFilter] = useState('');
  const [systemIdFilter, setSystemIdFilter] = useState('');
  const [retryTrigger, setRetryTrigger] = useState(0);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const workspacesService = useMemo(
    () => new WorkspacesService(fetchApi, microsoftAuthApi, discoveryApi, catalogApi),
    [fetchApi, microsoftAuthApi, discoveryApi, catalogApi]
  );

  const { value: workspaceSystems, loading: systemsLoading, error: systemsError } = useAsync(async () => {
    if (!environment || !workspaceId) return undefined;
    return await workspacesService.getWorkspaceSystems(environment, workspaceId);
  }, [environment, workspaceId, retryTrigger, workspacesService]);

  const { value: busySystemIds } = useAsync(async (): Promise<Set<string>> => {
    if (!environment || !workspaceId) return new Set();

    const pageRequest = { page: 1, pageSize: 100 };
    const [pendingResult, runningResult] = await Promise.all([
      workspacesService.getProvisions(environment, workspaceId, pageRequest, 'PENDING'),
      workspacesService.getProvisions(environment, workspaceId, pageRequest, 'RUNNING'),
    ]);

    const busyIds = new Set<string>();
    [...(pendingResult?.items ?? []), ...(runningResult?.items ?? [])].forEach(provision => {
      provision.systemIds?.split(',')
        .map(id => id.trim())
        .filter(id => !!id)
        .forEach(id => busyIds.add(id));
    });

    return busyIds;
  }, [environment, workspaceId, retryTrigger, workspacesService]);

  const isSystemBusy = useCallback((systemId: string): boolean => {
    return busySystemIds?.has(systemId) ?? false;
  }, [busySystemIds]);

  // Silent refresh polling para sistemas
  useEffect(() => {
    if (!environment || !workspaceId || systemsLoading || systemsError) return;
    const intervalId = setInterval(() => {
      setRetryTrigger(prev => prev + 1);
    }, 60_000);
    return () => clearInterval(intervalId);
  }, [environment, workspaceId, systemsLoading, systemsError]);

  const { value: addonsTitleMap } = useAsync(async () => {
    const response = await catalogApi.getEntities({
      fields: [
        'metadata.title',
        'metadata.annotations.open.totvs.io/app-code',
      ],
      filter: {
        kind: 'System',
        'metadata.annotations.open.totvs.io/addon-system': 'true',
      }
    });

    const map = new Map<string, string>();

    response.items.forEach(entity => {
      const addonId = entity.metadata.annotations?.['open.totvs.io/app-code'];
      if (addonId && entity.metadata.title) {
        map.set(addonId, entity.metadata.title);
      }
    });

    return map;
  }, [catalogApi]);

  const { value: catalogSystems } = useAsync(async () => {
    const response = await catalogApi.getEntities({
      fields: [
        'kind',
        'metadata.title',
        'metadata.name',
        'metadata.namespace',
        'metadata.annotations.open.totvs.io/app-code',
        'metadata.annotations.open.totvs.io/addons',
        'metadata.annotations.open.totvs.io/insights-databases',
        'metadata.annotations.open.totvs.io/datasharing-databases',
        'metadata.annotations.open.totvs.io/insights-folder-name',
      ],
      filter: {
        kind: 'System',
        'metadata.annotations.open.totvs.io/provisionable': 'true',
        'metadata.annotations.open.totvs.io/app-code': CATALOG_FILTER_EXISTS,
      },
      order: {
        field: 'metadata.title',
        order: 'asc',
      }
    });
    return response.items;
  }, [catalogApi]);

  const systemsMap = useMemo(() => {
    if (!catalogSystems) return new Map<string, SystemInfo>();

    const map = new Map<string, SystemInfo>();
    catalogSystems.forEach(entity => {
      const systemId = entity.metadata.annotations?.['open.totvs.io/app-code'];

      if (systemId) {
        const insightsResourceInfo = workspacesService.extractInsightsDatabaseInfo(environment!, entity);
        const insightsFolder = entity.metadata.annotations?.['open.totvs.io/insights-folder-name'];

        const addons = entity.metadata.annotations?.['open.totvs.io/addons']
          ?.split(',')
          ?.map(item => item.trim())
          ?.filter(item => !!item) || [];

        map.set(systemId, {
          title: entity.metadata.title,
          name: entity.metadata.name,
          namespace: entity.metadata.namespace,
          addons: addons,
          insightsFolder: insightsFolder,
          insightsResourceInfo: insightsResourceInfo,
        } as SystemInfo);
      }
    });
    return map;
  }, [catalogSystems, environment]);

  const availableSystems = useMemo(() => {
    if (!catalogSystems) return [];
    if (!workspaceSystems) return catalogSystems;

    return catalogSystems.filter((entity: any) =>
      workspaceSystems?.items.every((wsSystem: any) => wsSystem.systemId !== entity.metadata.annotations?.['open.totvs.io/app-code'])
    );
  }, [catalogSystems, workspaceSystems]);

  const getInsightsAddonComponents = useCallback(async (systemInfo: SystemInfo): Promise<AddonComponent[]> => {
    return await Promise.all(
      systemInfo?.insightsResourceInfo
        .map(async (resourceInfo: { name: string; resourceRef: string, type: ComponentType[] }) => {
          const { name, resourceRef, type } = resourceInfo;

          const database = await catalogApi.getEntityByRef(resourceRef);

          const instanceRef = database?.spec?.dependsOn?.toString();
          const instance = instanceRef
            ? await catalogApi.getEntityByRef(instanceRef)
            : null;

          const externalIp = instance?.metadata.annotations?.[GCPResourceAnnotations.SQL_INSTANCE_IP_PRIMARY];
          const internalIp = instance?.metadata.annotations?.[GCPResourceAnnotations.SQL_INSTANCE_IP_PRIVATE];

          return {
            name: name || '',
            ref: resourceRef || '',
            databaseName: database?.metadata.name || '',
            type,
            internalUrl: `${internalIp}:5432/${database?.metadata.name || ''}`,
            externalUrl: `${externalIp}:5432/${database?.metadata.name || ''}`,
          };
        })
    );
  }, [catalogApi]);

  const getAddonInfo = useCallback(async (systemId: string, addonId: string): Promise<Addon> => {
    const systemInfo = systemsMap.get(systemId);

    if (!systemInfo) {
      throw new Error(`System ${systemId} not found in catalog`);
    }

    const instanceItems = isInsightsAddon(addonId)
      ? await getInsightsAddonComponents(systemInfo)
      : [];

    return {
      id: addonId,
      name: addonId,
      ref: `system:${systemInfo.namespace}/${systemInfo.name}`,
      insightsProductFolderName: systemInfo.insightsFolder,
      components: instanceItems
    };
  }, [getInsightsAddonComponents, systemsMap]);

  const getSystemTitle = useCallback((systemId: string): string => {
    return systemsMap.get(systemId)?.title || systemId;
  }, [systemsMap]);

  const getAddonTitle = useCallback((addonId: string): string => {
    return addonsTitleMap?.get(addonId) || addonId;
  }, [addonsTitleMap]);

  const getSystemInfoOrThrow = useCallback(
    (systemId: string): SystemInfo => {
      const systemInfo = systemsMap.get(systemId);
      if (!systemInfo) {
        throw new Error(`System ${systemId} not found in catalog`);
      }
      return systemInfo;
    },
    [systemsMap]
  );

  const buildSystemPayload = useCallback(
    (systemId: string) => {
      const systemInfo = getSystemInfoOrThrow(systemId);
      return {
        id: systemId,
        ref: `system:${systemInfo.namespace}/${systemInfo.name}`,
        name: systemInfo.name,
      };
    },
    [getSystemInfoOrThrow]
  );

  const buildCatalogDataPayload = useCallback(
    async (systemId: string, addonId?: string): Promise<CatalogData> => {
      const systemPayload = buildSystemPayload(systemId);
      return {
        system: systemPayload,
        ...(addonId && { addon: await getAddonInfo(systemId, addonId) }),
      };
    },
    [buildSystemPayload, getAddonInfo]
  );

  const updateAddonOverrideState = useCallback((systemId: string, addonId: string, shouldEnable: boolean) => {
    setAddonStatesOverride(prev => {
      const currentAddons = prev[systemId] ?? (workspaceSystems?.items.find(s => s.systemId === systemId)?.addons ?? []);
      const nextAddons = shouldEnable
        ? [...currentAddons, addonId]
        : currentAddons.filter(id => id !== addonId);

      return { ...prev, [systemId]: nextAddons };
    });
  }, [workspaceSystems?.items]);

  const filteredSystems = useMemo(() => {
    if (!workspaceSystems?.items) return [];

    return workspaceSystems.items.filter(system => {
      const statusMatch = !statusFilter
        || (statusFilter === 'active' && system.active)
        || (statusFilter === 'disabled' && !system.active);

      const systemIdMatch = !systemIdFilter
        || system.systemId.toLowerCase()
          .includes(systemIdFilter.toLowerCase())
        || systemsMap.get(system.systemId)?.title.toLowerCase()
          .includes(systemIdFilter.toLowerCase());

      return statusMatch && systemIdMatch;
    });
  }, [workspaceSystems?.items, statusFilter, systemIdFilter, systemsMap]);

  const handleRetrySystemsLoad = useCallback(() => {
    setRetryTrigger(prev => prev + 1);
    setAddonStatesOverride({});
  }, []);

  const toggleSystem = useCallback((systemId: string) => {
    setExpandedSystems(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(systemId)) {
        newExpanded.delete(systemId);
      } else {
        newExpanded.add(systemId);
      }
      return newExpanded;
    });
  }, []);

  const enableAddon = useCallback(async (systemId: string, addonId: string) => {
    if (!environment || !workspaceId) {
      showError('Environment or workspace ID is missing');
      return;
    }

    if (!addonId) {
      showError('Addon ID is missing');
      return;
    }

    setIsTogglingAddon(true);

    try {
      const payload = await buildCatalogDataPayload(systemId, addonId);

      await workspacesService.provisionAddon(environment, workspaceId, payload);
      updateAddonOverrideState(systemId, addonId, true);
      showSuccess(`Addon ${getAddonTitle(addonId)} enabled successfully`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      showError(`Failed to enable addon: ${errorMessage}`);
    } finally {
      setIsTogglingAddon(false);
    }
  }, [environment, workspaceId, buildCatalogDataPayload, workspacesService, updateAddonOverrideState, getAddonTitle, showSuccess, showError]);

  const disableAddon = useCallback(async (systemId: string, addonId: string) => {
    if (!environment || !workspaceId) {
      showError('Environment or workspace ID is missing');
      return;
    }

    if (!addonId) {
      showError('Addon ID is missing');
      return;
    }

    setIsTogglingAddon(true);

    try {
      const payload = await buildCatalogDataPayload(systemId, addonId);

      await workspacesService.unprovisionAddon(environment, workspaceId, payload);
      updateAddonOverrideState(systemId, addonId, false);
      showSuccess(`Addon ${getAddonTitle(addonId)} disabled successfully`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      showError(`Failed to disable addon: ${errorMessage}`);
    } finally {
      setIsTogglingAddon(false);
    }
  }, [environment, workspaceId, buildCatalogDataPayload, workspacesService, updateAddonOverrideState, getAddonTitle, showSuccess, showError]);

  const reprocessInsightsTables = useCallback(async (systemId: string, addonId: string) => {
    if (!environment || !workspaceId) {
      showError('Environment or workspace ID is missing');
      return;
    }

    setIsReprocessingInsights(true);

    try {
      const payload: ReprocessAddon = {
        catalogData: await buildCatalogDataPayload(systemId, addonId),
        workspaces: [workspaceId],
      };

      await workspacesService.reprocessTables(environment, payload);
      showSuccess(`${getAddonTitle(addonId)} reprocessing started successfully`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      showError(`Failed to reprocess tables: ${errorMessage}`);
    } finally {
      setIsReprocessingInsights(false);
    }
  }, [environment, workspaceId, buildCatalogDataPayload, workspacesService, getAddonTitle, showSuccess, showError]);

  const removeSystem = useCallback(async (systemId: string, systemTitle: string) => {
    if (!environment || !workspaceId) {
      showError('Environment or workspace ID is missing');
      return;
    }

    setIsRemovingSystem(true);

    try {
      const payload = await buildCatalogDataPayload(systemId);

      await workspacesService.removeWorkspaceSystem(environment, workspaceId, payload);
      showSuccess(`System ${systemTitle} removal started successfully`);
      handleRetrySystemsLoad();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      showError(`Failed to remove system: ${errorMessage}`);
    } finally {
      setIsRemovingSystem(false);
    }
  }, [environment, workspaceId, buildCatalogDataPayload, workspacesService, showSuccess, showError, handleRetrySystemsLoad]);

  const handleAddonToggleClick = useCallback((systemId: string, addonId: string, isEnabled: boolean) => {
    const systemTitle = getSystemTitle(systemId);
    const addonTitle = getAddonTitle(addonId);
    const action = isEnabled ? 'disable' : 'enable';
    
    setDialogData({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Addon`,
      text: `This will ${action} the ${addonTitle} addon for ${systemTitle} in this workspace. Do you want to continue?`,
      onConfirm: () => isEnabled ? disableAddon(systemId, addonId) : enableAddon(systemId, addonId),
    });
  }, [getSystemTitle, getAddonTitle, disableAddon, enableAddon]);

  const handleReprocessClick = useCallback((systemId: string, addonId: string) => {
    const systemTitle = getSystemTitle(systemId);
    const addonTitle = getAddonTitle(addonId);
    
    setDialogData({
      title: 'Reprocess tables',
      text: `This will update the list of tables used by Insights and Data Sharing in this workspace for ${addonTitle} in ${systemTitle}. Do you want to continue?`,
      onConfirm: () => reprocessInsightsTables(systemId, addonId),
    });
  }, [getSystemTitle, getAddonTitle, reprocessInsightsTables]);

  const handleAddSystem = useCallback(() => {
    setIsAddModalOpen(true);
  }, []);

  const handleRemoveSystemClick = useCallback((system: WorkspaceSystemItem) => {
    const systemTitle = getSystemTitle(system.systemId);
    setDialogData({
      title: 'Remove system?',
      text: `You are about to remove ${systemTitle} from the workspace. This will revoke access for all users to this system. Do you want to continue?`,
      onConfirm: () => removeSystem(system.systemId, systemTitle),
    });
  }, [getSystemTitle, removeSystem]);

  const handleRetrySystemProvision = useCallback(async (system: WorkspaceSystemItem) => {
    if (!environment || !workspaceId) {
      showError('Environment or workspace ID is missing');
      return;
    }

    try {
      const payload: RetrySystemProvisionCommand = {
        catalogData: {
          system: buildSystemPayload(system.systemId),
        },
      };

      await workspacesService.retrySystemProvision(
        environment,
        workspaceId,
        payload
      );
      showSuccess('System provision retry requested successfully.');
      handleRetrySystemsLoad();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      showError(`Failed to retry system provision: ${errorMessage}`);
    }
  }, [environment, workspaceId, buildSystemPayload, workspacesService, showError, showSuccess, handleRetrySystemsLoad]);

  const handleAddSystemConfirm = useCallback((data: { systemId: string; title: string }) => {
    showSuccess(`System ${data.title} (${data.systemId}) added successfully`);
    setIsAddModalOpen(false);
    setRetryTrigger(prev => prev + 1);
    setAddonStatesOverride({});
  }, [showSuccess]);

  const handleAddSystemCancel = useCallback(() => {
    setIsAddModalOpen(false);
  }, []);

  const handleStatusFilterChange = useCallback((event: React.ChangeEvent<{ value: unknown }>) => {
    setStatusFilter(event.target.value as string);
  }, []);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSystemIdFilter(event.target.value);
  }, []);

  const renderAddon = useCallback((system: WorkspaceSystemItem, addonId: string) => {
    const key = `${system.systemId}-${addonId}`;
    const effectiveAddons = addonStatesOverride[system.systemId] ?? system.addons;
    const isEnabled = effectiveAddons?.includes(addonId) ?? false;
    const systemBusy = busySystemIds?.has(system.systemId) ?? false;

    const switchDisabled = !system.active || !isWorkspaceActive || systemBusy;
    const canReprocess = isInsightsAddon(addonId) && canReprocessTables;
    const reprocessDisabled = !canReprocess || !isEnabled || switchDisabled || isReprocessingInsights;
    
    let tooltipTitle = '';
    if (!isWorkspaceActive) {
      tooltipTitle = 'This operation is disabled because the workspace is not active';
    } else if (systemBusy) {
      tooltipTitle = 'This operation is disabled because there is a provision in progress for this system';
    }

    let reprocessTooltipTitle = 'Reprocess tables';
    if (!isEnabled) {
      reprocessTooltipTitle = 'Available only when Insights is enabled';
    } else if (!isWorkspaceActive) {
      reprocessTooltipTitle = 'This operation is disabled because the workspace is not active';
    } else if (systemBusy) {
      reprocessTooltipTitle = 'This operation is disabled because there is a provision in progress for this system';
    } else if (isReprocessingInsights) {
      reprocessTooltipTitle = 'A reprocess request is already running';
    }

    return (
      <Box
        key={key}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        className={classes.addonRow}
        p={1}
        mb={1}
        borderRadius={4}
      >
        <Typography variant="body2">
          {`${getAddonTitle(addonId)} (${addonId})`}
        </Typography>
        <Box className={classes.addonActions}>
          <Tooltip title={tooltipTitle}>
            <FormControlLabel
              control={
                <Switch
                  checked={isEnabled}
                  onChange={() => handleAddonToggleClick(system.systemId, addonId, isEnabled)}
                  disabled={switchDisabled}
                  color="primary"
                  size="small"
                />
              }
              label={isEnabled ? 'Enabled' : 'Disabled'}
              labelPlacement="start"
            />
          </Tooltip>
          <Box className={classes.addonReprocessColumn}>
            {canReprocess && (
              <Tooltip title={reprocessTooltipTitle}>
                <span>
                  <IconButton
                    color="primary"
                    size="small"
                    disabled={reprocessDisabled}
                    onClick={() => handleReprocessClick(system.systemId, addonId)}
                  >
                    <AutorenewIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>
    );
  }, [addonStatesOverride, busySystemIds, canReprocessTables, classes.addonActions, classes.addonReprocessColumn, classes.addonRow, getAddonTitle, handleAddonToggleClick, handleReprocessClick, isWorkspaceActive, isReprocessingInsights]);

  const renderSystemRow = useCallback((system: WorkspaceSystemItem) => {
    const systemEntity = systemsMap.get(system.systemId);
    const systemTitle = getSystemTitle(system.systemId);
    const displayName = systemTitle && systemTitle !== system.systemId
      ? `${systemTitle} (${system.systemId})`
      : system.systemId;
    const systemBusy = isSystemBusy(system.systemId);

    let actionTooltipTitle = '';
    if (!isWorkspaceActive) {
      actionTooltipTitle = 'This operation is disabled because the workspace is not active';
    } else if (systemBusy) {
      actionTooltipTitle = 'This operation is disabled because there is a provision in progress for this system';
    }

    return (
      <React.Fragment key={system.systemId}>
        <TableRow>
          <TableCell className={classes.expandCell}>
            <IconButton size="small" onClick={() => toggleSystem(system.systemId)}>
              {expandedSystems.has(system.systemId) ? (
                <KeyboardArrowUpIcon />
              ) : (
                <KeyboardArrowDownIcon />
              )}
            </IconButton>
          </TableCell>
          <TableCell>
            <Typography variant="body2">{displayName}</Typography>
          </TableCell>
          <TableCell>
            {canCreate && !system.active && (
              <Tooltip title={actionTooltipTitle}>
                <span>
                  <Button
                    variant="text"
                    color="primary"
                    size="small"
                    disabled={!isWorkspaceActive}
                    onClick={() => handleRetrySystemProvision(system)}
                  >
                    retry
                  </Button>
                </span>
              </Tooltip>
            )}

            {canCreate && system.active && (
              <Tooltip title={actionTooltipTitle}>
                <span>
                  <Button
                    variant="text"
                    color="primary"
                    size="small"
                    disabled={!isWorkspaceActive || systemBusy}
                    onClick={() => handleRemoveSystemClick(system)}
                  >
                    remove
                  </Button>
                </span>
              </Tooltip>
            )}
          </TableCell>
          <TableCell>
            <Chip
              label={system.active ? 'Active' : 'Disabled'}
              className={system.active ? classes.statusActive : classes.statusInactive}
              size="small"
            />
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
            <Collapse in={expandedSystems.has(system.systemId)} timeout="auto" unmountOnExit>
              <Box className={classes.addonContainer}>
                {systemEntity?.addons?.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No addons available
                  </Typography>
                ) : (
                  systemEntity?.addons.map((addonId: string) => renderAddon(system, addonId))
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }, [
    canCreate,
    classes,
    expandedSystems,
    handleRemoveSystemClick,
    handleRetrySystemProvision,
    isSystemBusy,
    isWorkspaceActive,
    renderAddon,
    getSystemTitle,
    systemsMap,
    toggleSystem,
  ]);

  return (
    <Card>
      <CardHeader title="System Information" />
      <CardContent>
        <Box className={classes.filterContainer}>
          <Box className={classes.filterRow}>
            <FormControl className={classes.formControl} variant="outlined" size="small">
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                label="Status"
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="disabled">Disabled</MenuItem>
              </Select>
            </FormControl>

            <TextField
              className={classes.searchField}
              variant="outlined"
              size="small"
              placeholder="System"
              value={systemIdFilter}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {canCreate && (
              <Box className={classes.addButtonContainer}>
                <Tooltip title={!isWorkspaceActive ? 'This operation is disabled because the workspace is not active' : ''}>
                  <span>
                    <Button
                      className={classes.addButton}
                      variant="contained"
                      color="primary"
                      size="medium"
                      disabled={!isWorkspaceActive}
                      onClick={handleAddSystem}
                      startIcon={<AddIcon />}
                    >
                      Add System
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>

        <Box className={classes.tableContainer}>
          <Table size="small" className={classes.table}>
            <TableBody>
              {systemsLoading && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" style={{ marginLeft: 16 }}>
                        Loading systems...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {!systemsLoading && !!systemsError && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Paper style={{ padding: 16, backgroundColor: '#ffebee' }}>
                      <Typography variant="body2" color="error" style={{ marginBottom: 8 }}>
                        Error loading systems: {systemsError?.message}
                      </Typography>
                      <Button
                        color="primary"
                        size="small"
                        variant="outlined"
                        onClick={handleRetrySystemsLoad}
                      >
                        Retry
                      </Button>
                    </Paper>
                  </TableCell>
                </TableRow>
              )}

              {!systemsLoading && !systemsError && !filteredSystems.length && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No systems found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!systemsLoading && !systemsError && filteredSystems.length > 0 && (
                filteredSystems.map(renderSystemRow)
              )}
            </TableBody>
          </Table>
        </Box>

        <Backdrop className={classes.backdrop} open={isTogglingAddon || isRemovingSystem || isReprocessingInsights}>
          <CircularProgress color="inherit" />
          <Typography variant="body1">Processing, please wait...</Typography>
        </Backdrop>
        
        <SimpleConfirmDialog
          open={dialogData !== undefined}
          title={dialogData?.title || ''}
          text={dialogData?.text || ''}
          onCancel={() => {
            setDialogData(undefined);
          }}
          onConfirm={() => {
            dialogData?.onConfirm();
            setDialogData(undefined);
          }}
        />

        <NotificationSnackbar notification={notification} onClose={hideNotification} />

        <WorkspaceSystemAdd
          open={isAddModalOpen}
          onClose={handleAddSystemCancel}
          onAdd={handleAddSystemConfirm}
          environment={environment}
          workspaceId={workspaceId}
          availableSystems={availableSystems}
        />
      </CardContent>
    </Card>
  );
};